import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { PageType } from "../constants";
import { CrmApplicationStatus } from "../models/crmStatus";
import { IdAzureIntroContactDataType } from "../models/forms";
import { Programs } from "../models/programsConfig";
import {
  AnyStatusData,
  IdAzureApplicationStep,
  IdAzureApplicationStepsOrder,
} from "../models/status";
import {
  IdAzureIntroFormId,
  IdAzureIntroSubFormsId,
} from "../pages/IdAzureIntroPage/IdAzureIntroPageConstants";
import { statusActions } from "../redux/actions/statusActions";
import { OnboardingRootState } from "../redux/reducers/rootReducer";
import {
  getCrmApplicationStatus,
  getIdAzureApplicationStep,
  getIsInitialCrmFetchDone,
  getOnboardingDecision,
  getSubFormData,
} from "../redux/selectors";
import { getOnboardingSignedIn } from "../redux/selectors/authSelectors";
import { OnboardingDecisionChoice } from "../server/crm/dataService/models/staticOptions";
import { compareCrmApplicationStatus } from "./commonStatusHelpers";
import { isIdAzureIntroContactAndStudioFormValid } from "./formValidation";

export const useIsIdAzureComponentLocked = (currentStatusLockRule?: AnyStatusData): boolean => {
  const currentStep = useSelector(getIdAzureApplicationStep);
  const isSignedIn = useSelector(getOnboardingSignedIn);
  const step = currentStatusLockRule?.step[Programs.IdAzure] ?? IdAzureApplicationStep.Unset;
  // For now, being signed in for these components is a must regardless
  return !isSignedIn
    ? true
    : currentStep === undefined ||
        (currentStatusLockRule !== undefined &&
          IdAzureApplicationStepsOrder.indexOf(step) >
            IdAzureApplicationStepsOrder.indexOf(currentStep));
};

export const compareIdAzureApplicationStepStatus = (
  step1: IdAzureApplicationStep,
  step2: IdAzureApplicationStep
) => {
  const indexStep1 = IdAzureApplicationStepsOrder.indexOf(step1);
  const indexStep2 = IdAzureApplicationStepsOrder.indexOf(step2);
  return indexStep1 < indexStep2 ? -1 : indexStep1 > indexStep2 ? 1 : 0;
};

export const calculateIdAzureApplicationStep = async (
  currentCrmIdAzureApplicationStatus: CrmApplicationStatus | undefined,
  idAzureIntroContactSubFormData: IdAzureIntroContactDataType,
  onboardingDecision: OnboardingDecisionChoice | undefined
) => {
  /** ********************************** Steps logic ************************************/
  if (currentCrmIdAzureApplicationStatus === CrmApplicationStatus.Rejected) {
    return IdAzureApplicationStep.ApplicationRejected;
  }

  // NDA under review
  const isNdaUnderReview = currentCrmIdAzureApplicationStatus
    ? compareCrmApplicationStatus(
        currentCrmIdAzureApplicationStatus,
        CrmApplicationStatus.NdaUnderReview
      ) === 0
    : false;
  if (isNdaUnderReview) {
    return IdAzureApplicationStep.NdaUnderReview;
  }

  // NDA sent
  const isNdaSent = currentCrmIdAzureApplicationStatus
    ? compareCrmApplicationStatus(
        currentCrmIdAzureApplicationStatus,
        CrmApplicationStatus.NdaSent
      ) === 0
    : false;
  if (isNdaSent) {
    return IdAzureApplicationStep.NdaSent;
  }

  // NDA signed
  const isNdaSigned = currentCrmIdAzureApplicationStatus
    ? compareCrmApplicationStatus(
        currentCrmIdAzureApplicationStatus,
        CrmApplicationStatus.NdaSigned
      ) === 0
    : false;

  if (!isNdaSigned) {
    // Introduction (Contact + Studio) -> Intro ready to submit
    const isIdAzureContactFormValid = await isIdAzureIntroContactAndStudioFormValid(
      idAzureIntroContactSubFormData
    );

    return !isIdAzureContactFormValid
      ? IdAzureApplicationStep.Introduction
      : IdAzureApplicationStep.IntroductionReadyToSubmit;
  }

  /* Normally if an NDA was already signed, the application would be complete.
  But because an NDA could potentially be signed through a different program (i.e ID Xbox),
  then we need to double check for the Onboarding Decision (exclusive to ID Azure)
  at the final step again. */
  // NDA signed -> Application complete
  return onboardingDecision !== OnboardingDecisionChoice.approved
    ? IdAzureApplicationStep.NdaSigned
    : IdAzureApplicationStep.ApplicationComplete;
};

export const useCalculateIdAzureStatus = async (pageType: PageType | undefined) => {
  const dispatch = useDispatch();

  const isSignedIn: boolean = useSelector(getOnboardingSignedIn);
  const currentStep: IdAzureApplicationStep | undefined = useSelector(getIdAzureApplicationStep);
  const isInitialCrmFetchDone = useSelector(getIsInitialCrmFetchDone);

  // ID@Azure Introduction sub form data
  const idAzureIntroContactSubFormData = useSelector((state: OnboardingRootState) =>
    getSubFormData(state, IdAzureIntroFormId, IdAzureIntroSubFormsId.ContactAndStudio)
  ) as IdAzureIntroContactDataType;

  // Application submission
  const currentCrmIdAzureApplicationStatus = useSelector(getCrmApplicationStatus);

  const onboardingDecision = useSelector(getOnboardingDecision);

  useEffect(() => {
    const calculateNextStep = async () => {
      // 0: Must be signed in
      if (
        !pageType ||
        !isSignedIn ||
        pageType !== PageType.IdAzureApplication ||
        !isInitialCrmFetchDone
      ) {
        return;
      }

      const step = await calculateIdAzureApplicationStep(
        currentCrmIdAzureApplicationStatus,
        idAzureIntroContactSubFormData,
        onboardingDecision
      );

      if (step !== currentStep) {
        dispatch(statusActions.updateStep(step, Programs.IdAzure));
      }
    };

    calculateNextStep();
  }, [
    pageType,
    currentCrmIdAzureApplicationStatus,
    currentStep,
    dispatch,
    idAzureIntroContactSubFormData,
    isSignedIn,
    onboardingDecision,
    isInitialCrmFetchDone,
  ]);
};
